import { createTheme } from '@mui/material/styles';
import { paletteOf } from './palettes';

// SSATWeb design tokens (redesign 2026-10). One indigo accent, slate neutrals, colour only for status.
export const SIDEBAR_WIDTH = 248;

const FONT = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const light = {
  mode: 'light',
  primary: { main: '#4f46e5', dark: '#4338ca', light: '#818cf8', contrastText: '#ffffff' },
  secondary: { main: '#0ea5e9', dark: '#0284c7', light: '#7dd3fc', contrastText: '#ffffff' },
  success: { main: '#16a34a', dark: '#15803d', light: '#4ade80', contrastText: '#ffffff' },
  warning: { main: '#d97706', dark: '#b45309', light: '#fbbf24', contrastText: '#ffffff' },
  error: { main: '#dc2626', dark: '#b91c1c', light: '#f87171', contrastText: '#ffffff' },
  info: { main: '#0284c7', dark: '#0369a1', light: '#38bdf8', contrastText: '#ffffff' },
  background: { default: '#f5f6fa', paper: '#ffffff' },
  text: { primary: '#0f172a', secondary: '#475569' },
  divider: '#e2e8f0',
  grey: { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1' },
};

const dark = {
  mode: 'dark',
  primary: { main: '#818cf8', dark: '#6366f1', light: '#a5b4fc', contrastText: '#0b1020' },
  secondary: { main: '#38bdf8', dark: '#0ea5e9', light: '#7dd3fc', contrastText: '#0b1020' },
  success: { main: '#4ade80', dark: '#22c55e', light: '#86efac', contrastText: '#0b1020' },
  warning: { main: '#fbbf24', dark: '#f59e0b', light: '#fcd34d', contrastText: '#0b1020' },
  error: { main: '#f87171', dark: '#ef4444', light: '#fca5a5', contrastText: '#0b1020' },
  info: { main: '#38bdf8', dark: '#0ea5e9', light: '#7dd3fc', contrastText: '#0b1020' },
  background: { default: '#0b1020', paper: '#131a2e' },
  text: { primary: '#e5e7eb', secondary: '#94a3b8' },
  divider: '#243049',
  grey: { 50: '#1a2238', 100: '#1e2740', 200: '#243049', 300: '#33415c' },
};

export function createAppTheme(mode = 'light', paletteName) {
  const pal = paletteOf(paletteName);
  const base = mode === 'dark' ? dark : light;
  const pr = mode === 'dark' ? pal.darkPrimary : pal.primary;
  const palette = { ...base, primary: { main: pr.main, dark: pr.dark, light: pr.light, contrastText: pr.contrastText }, secondary: { ...base.secondary, main: pal.secondary } };
  return createTheme({
    palette,
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: FONT,
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 650, letterSpacing: '-0.01em' },
      h6: { fontWeight: 650, letterSpacing: '-0.005em' },
      subtitle1: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: palette.background.default, fontFeatureSettings: '"cv02","cv03","cv04","cv11"' },
          '::selection': { backgroundColor: palette.primary.main, color: '#fff' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: palette.divider },
          elevation1: { border: `1px solid ${palette.divider}`, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' },
          elevation2: { border: `1px solid ${palette.divider}`, boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)' },
          elevation3: { border: `1px solid ${palette.divider}`, boxShadow: '0 4px 16px rgba(15,23,42,0.08)' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { border: `1px solid ${palette.divider}`, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', borderRadius: 12 } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingLeft: 16, paddingRight: 16 },
          containedPrimary: {
            background: 'var(--btn-grad)', color: 'var(--btn-text)', boxShadow: '0 4px 14px rgb(var(--c-primary-rgb) / 0.35)',
            transition: 'transform .2s ease, box-shadow .2s ease, filter .2s ease',
            '&:hover': { background: 'var(--btn-grad-hover)', transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgb(var(--c-primary-rgb) / 0.45)' },
            '&:active': { transform: 'translateY(0)' },
            '&.Mui-disabled': { background: palette.grey[200], boxShadow: 'none', color: palette.text.secondary },
          },
          outlined: { borderColor: palette.divider, color: palette.text.primary, '&:hover': { borderColor: palette.primary.main, backgroundColor: 'rgb(var(--c-primary-rgb) / 0.06)' } },
        },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8, backgroundColor: palette.background.paper } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${palette.divider}` },
          head: {
            fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
            color: palette.text.secondary, backgroundColor: palette.grey[50],
          },
        },
      },
      MuiTableRow: { styleOverrides: { root: { '&:last-child td': { borderBottom: 0 } } } },
      MuiTooltip: { styleOverrides: { tooltip: { fontSize: 12, borderRadius: 8 } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 8, height: 6 } } },
    },
  });
}

// Chart colours for the active palette (Chart.js datasets).
export const chartColors = (paletteName) => paletteOf(paletteName).chart;
export const chartColorsSoft = (paletteName) => chartColors(paletteName).map(c => c + 'd9');
